import { Filters, FiltersOptions } from '@lidea/shared/util/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { filtersAPI } from '../../apis/filters-api';
import useFilterStore from '../../filters-store/filters-store';
import { filterKeys } from './../../keys/filter-keys';

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

type LocalizedName = {
  name_fr?: string | null;
  name_en?: string | null;
};

function getLocalizedLabel(
  item: LocalizedName,
  locale: 'fr' | 'en'
): string {
  return item[`name_${locale}`] || item.name_fr || item.name_en || '';
}

export function useFilters() {
  const { i18n } = useTranslation([], { useSuspense: false });

  const selectedFilters = useFilterStore((state) => state.selectedFilters);

  const locale = useMemo(() => i18n.language as 'fr' | 'en', [i18n.language]);
  const filters = useQuery<Filters>({
    queryKey: filterKeys.search(selectedFilters),
    queryFn: () => filtersAPI.getAll(selectedFilters),
    placeholderData: keepPreviousData,
  });

  // const contractCode = filters.data?.protocol.map(item => item.contract_code) ;
  // const uniqueContractCode = Array.from(new Set(contractCode));

  const filtersAsOptions = useMemo<FiltersOptions>(() => {
    if (!filters.data) {
      return {};
    }

    return (Object.entries(filters.data) as Entries<Filters>).reduce(
      (acc, curr) => {
        const [key, values] = curr;

        switch (key) {
          case 'country': {
            return {
              ...acc,
              [key]: {
                label: key,
                value: key,
                options: (Object.entries(values) as Entries<typeof values>).map(
                  (country) => ({
                    label: country[1] ?? '',
                    value: country[0],
                  })
                ),
              },
            };
          }

          case 'country_manager':
          case 'manager': {
            return {
              ...acc,
              [key]: {
                label: key,
                value: key,
                options: values.map((item) => ({
                  label: `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim(),
                  value: item.id,
                })),
              },
            };
          }
          case 'farm': {
            return {
              ...acc,
              [key]: {
                label: key,
                value: key,
                options: values.map((item) => ({
                  label: item.name ?? '',
                  value: item.id,
                })),
              },
            };
          }

          case 'contract_code': {
            return {
              ...acc,
              [key]: {
                label: key,
                value: key,
                options: values.map((item) => ({
                  label: getLocalizedLabel(item, locale),
                  value: item.id,
                })),
              },
            };
          }

          case 'analysis_status':
          case 'plot_color': {
            return {
              ...acc,
              [key]: {
                label: key,
                value: key,
                options: values.map((item) => ({
                  label: getLocalizedLabel(item, locale),
                  value: item.value,
                })),
              },
            };
          }

          // case 'protocol':
          case 'species_group':
          case 'generation':
          case 'variety':
          case 'species': {
            return {
              ...acc,
              [key]: {
                label: key,
                value: key,
                options: values.map((item) => ({
                  label: getLocalizedLabel(item, locale),
                  value: item.id,
                })),
              },
            };
          }

          default: {
            return {
              ...acc,
              [key]: {
                label: key,
                value: key,
                options: values.map((item) => ({
                  label: item.name ?? '',
                  value: item.id,
                })),
              },
            };
          }
        }
      },
      {}
    );
  }, [filters.data, locale]);

  return { ...filters, filtersAsOptions };
}

export default useFilters;
